import useTranslation from '../useTranslation';
import type { BatteryResponse } from 'generatedTypes/asset/models';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { Props as DetailsViewEntityLinkProps } from 'components/DetailsViewEntityLink/types';
import type { BoundaryPartData, Geometry } from 'components/Map/types';

import useApplications from 'hooks/useApplications';
import type { ApplicationResponse } from 'hooks/useApplications/types';
import useBattery from 'hooks/useBattery';
import useBoundary from 'hooks/useBoundary';
import usePageRoutes from 'hooks/usePageRoutes';

import { DETAIL_TYPE_SERIAL_NUMBER } from 'pages/BatteryDetailsPage/constants';

const useData = () => {
    const { batteryId } = useParams();
    const { EDIT_BATTERY } = useTranslation();
    const { fetchBattery, isLoading } = useBattery();
    const { assetsPage } = usePageRoutes();
    const {
        applications,
        fetch: fetchApplications,
        isLoading: isApplicationsLoading
    } = useApplications();
    const { fetchBoundary } = useBoundary();
    const navigate = useNavigate();
    const [currentBattery, setCurrentBattery] = useState<BatteryResponse | undefined>(undefined);
    const [isApplicationsOpen, setIsApplicationsOpen] = useState(false);
    const [isLinkedEntitiesOpen, setIsLinkedEntitiesOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedApplicationId, setSelectedApplicationId] = useState<string | undefined>(
        undefined
    );
    const [selectedBoundaryGeometry, setSelectedBoundaryGeometry] = useState<Geometry | undefined>(
        undefined
    );
    const isFirstLoad = useRef(true);

    const entitiesLink: DetailsViewEntityLinkProps[] = [
        {
            entityName: currentBattery?.serialNumber ?? '',
            entityType: DETAIL_TYPE_SERIAL_NUMBER
        }
    ];

    const getCurrentBattery = async () => {
        if (batteryId) {
            const battery = await fetchBattery({ id: batteryId });

            setCurrentBattery(battery ?? undefined);
            fetchApplications({ batteryId }).next();
        }
    };

    useEffect(() => {
        if (batteryId) {
            getCurrentBattery();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batteryId]);

    const handleSelectApplication = async (application: ApplicationResponse) => {
        setSelectedApplicationId(application.id);

        if (!application.boundaryId) {
            setSelectedBoundaryGeometry(undefined);

            return;
        }

        const boundary = await fetchBoundary({ id: application.boundaryId });

        setSelectedBoundaryGeometry(
            boundary
                ? { isEditable: false, parts: boundary.boundaryParts as BoundaryPartData[] }
                : undefined
        );
    };

    useEffect(() => {
        if (applications && applications.length > 0 && isFirstLoad.current) {
            isFirstLoad.current = false;
            handleSelectApplication(applications[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applications]);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleToggleApplications = () => {
        setIsApplicationsOpen(previous => !previous);
    };

    const handleToggleLinkedEntities = () => {
        setIsLinkedEntitiesOpen(previous => !previous);
    };

    const handleEditClose = () => {
        if (isEditing) {
            setIsEditing(false);
            setIsClosing(false);
            getCurrentBattery();
        }
    };

    const dropdownMenuItems = [
        {
            onClick: handleEditClick,
            text: EDIT_BATTERY
        }
    ];

    const handleNavigateBack = () => {
        navigate(assetsPage);
    };

    const handleClose = () => {
        if (isEditing) {
            setIsClosing(true);
        } else {
            handleNavigateBack();
        }
    };

    return {
        applications,
        currentBattery,
        dropdownMenuItems,
        entitiesLink,
        handleClose,
        handleEditClose,
        handleSelectApplication,
        handleToggleApplications,
        handleToggleLinkedEntities,
        isApplicationsLoading,
        isApplicationsOpen,
        isClosing,
        isEditing,
        isLinkedEntitiesOpen,
        isLoading,
        selectedApplicationId,
        selectedBoundaryGeometry,
        setIsClosing
    };
};

export default useData;
